// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { formGridProps } from "@store/Themes/common/props/form";
import constants from "@store/constants";
import useFieldMask from "@store/Framework/Common/Hooks/useFieldMask";
import useIsCSDS from "@store/Framework/Common/Hooks/useIsCSDS";
import useQueryString from "@toolkit/Framework/Common/Hooks/useQueryString";
import { useCurrentLanguage, useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useCurrentCountries } from "@toolkit/Framework/Store/Data/Countries/CountriesHooks";
import React, {
    ChangeEventHandler,
    createRef,
    FC,
    FormEventHandler,
    RefObject,
    useEffect,
    useRef,
    useState,
} from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch } from "react-redux";

const recaptchaLocation = "RequestQuote";

export interface RequestQuoteFormProps extends WidgetProps {}

export interface RequestQuoteFormStyles {
    agreementMessage?: TypographyPresentationProps;
    errorMessage?: InfosBoxProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    gridItemFull?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successMessage?: InfosBoxProps;
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
}

const requestQuoteFormStyles: RequestQuoteFormStyles = {
    successMessage: { type: "success" },
    errorMessage: { type: "error" },
    gridContainer: formGridProps.gridContainer,
    gridItem: { width: [12, 6, 6, 6, 6] },
    gridItemFull: { width: 12 },
    agreementMessage: { variant: "p" },
    submitButton: { variant: "primary" },
};

const styles = requestQuoteFormStyles;

const RequestQuoteForm: FC<RequestQuoteFormProps> = () => {
    const dispatch = useDispatch();
    const language = useCurrentLanguage()?.languageCode?.slice(0, 2).toUpperCase();
    const { sku, success } = useQueryString();
    const formRef = useRef<HTMLFormElement>(null);
    const showErrorsRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const { salesforceOrganizationId, salesforceSubmitUrl } = useSettingsCollection().customSettings || {};
    const recaptchaRef: RefObject<ReCAPTCHA> = createRef();
    const countries = useCurrentCountries();

    useEffect(() => {
        if (!countries) {
            dispatch(loadCurrentCountries());
        }
    }, []);

    const isCSDS = useIsCSDS();
    const siteName = isCSDS ? "CSDS" : "Cansel";
    const recordType = isCSDS ? "0122S0000009FyR" : "0122S000000NRF4";
    const countryAbbreviation = isCSDS ? "US" : "CA";
    const currencyCode = isCSDS ? "USD" : "CAD";

    // --- First Name Field

    const firstNameConstants = constants.formField.salesforce.firstName;

    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState("");

    const validateFirstName = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", firstNameConstants.label()).toString()
            : !firstNameConstants.regExp.test(value)
            ? firstNameConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setFirstNameError(msg);
        }

        return !msg;
    };

    const onChangeFirstName: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateFirstName(value);
        setFirstName(value);
    };

    // --- Last Name Field

    const lastNameConstants = constants.formField.salesforce.lastName;

    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState("");

    const validateLastName = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", lastNameConstants.label()).toString()
            : !lastNameConstants.regExp.test(value)
            ? lastNameConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setLastNameError(msg);
        }

        return !msg;
    };

    const onChangeLastName: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateLastName(value);
        setLastName(value);
    };

    // --- Email Field

    const emailConstants = constants.formField.email;

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");

    const validateEmail = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", emailConstants.label()).toString()
            : !emailConstants.regExp.test(value)
            ? emailConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setEmailError(msg);
        }

        return !msg;
    };

    const onChangeEmail: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateEmail(value);
        setEmail(value);
    };

    // --- Company Field

    const companyLabel = translate("Cansel_Form_Company");

    const [company, setCompany] = useState("");
    const [companyError, setCompanyError] = useState("");

    const validateCompany = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", companyLabel).toString() : "";

        if (showErrorsRef.current) {
            setCompanyError(msg);
        }

        return !msg;
    };

    const onChangeCompany: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateCompany(value);
        setCompany(value);
    };

    // --- Phone Field

    const phoneConstants = constants.formField.phone;

    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");

    const phoneMask = useFieldMask(phoneConstants.maskOptions, phone, setPhone);

    const validatePhone = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", phoneConstants.label()).toString()
            : !phoneConstants.regExp.test(value)
            ? phoneConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setPhoneError(msg);
        }

        return !msg;
    };

    const onChangePhone: ChangeEventHandler<HTMLInputElement> = event => {
        phoneMask.resolve(event.currentTarget.value);
        validatePhone(phoneMask.value);
        setPhone(phoneMask.value);
    };

    // --- State Field

    const stateTranslationKey = countryAbbreviation === "CA" ? "Province" : "State";
    const stateLabel = translate(`Cansel_Form_${stateTranslationKey}`);
    const statePlaceholder = translate(`Cansel_Form_${stateTranslationKey}_Placeholder`);

    const [state, setState] = useState("");
    const [stateError, setStateError] = useState("");

    const validateState = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", stateLabel).toString() : "";

        if (showErrorsRef.current) {
            setStateError(msg);
        }

        return !msg;
    };

    const onChangeState: ChangeEventHandler<HTMLSelectElement> = event => {
        const value = event.currentTarget.value;
        validateState(value);
        setState(value);
    };

    // --- Comment Field

    const [comment, setComment] = useState(translate("Cansel_RequestQuote_Comment_DefaultText", sku || ""));

    const onChangeComment: ChangeEventHandler<HTMLTextAreaElement> = event => {
        setComment(event.currentTarget.value);
    };

    // ---

    const submitHandler: FormEventHandler<HTMLFormElement> = event => {
        event.preventDefault();
        showErrorsRef.current = true;

        const isFirstNameValid = validateFirstName(firstName);
        const isLastNameValid = validateLastName(lastName);
        const isEmailValid = validateEmail(email);
        const isCompanyValid = validateCompany(company);
        const isPhoneValid = validatePhone(phone);
        const isStateValid = validateState(state);

        let isReCaptchaValid = false;
        dispatch(
            validateReCaptcha({
                location: recaptchaLocation,
                recaptchaRef,
                onSuccess: isValid => {
                    isReCaptchaValid = isValid;
                },
            }),
        );

        if (
            !isFirstNameValid ||
            !isLastNameValid ||
            !isEmailValid ||
            !isCompanyValid ||
            !isPhoneValid ||
            !isStateValid ||
            !isReCaptchaValid
        ) {
            return;
        }

        setIsLoading(true);
        formRef.current?.submit();
    };

    if (success === "true") {
        return <InfosBox {...styles.successMessage}>{siteMessage("Cansel_RequestQuote_Success")}</InfosBox>;
    }

    if (!sku) {
        return <InfosBox {...styles.errorMessage}>{siteMessage("Cansel_RequestQuote_MissingSKU")}</InfosBox>;
    }

    if (!salesforceOrganizationId || !salesforceSubmitUrl) {
        return (
            <InfosBox {...styles.errorMessage}>{translate("An error has occurred, please try again later.")}</InfosBox>
        );
    }

    const states = countries?.find(x => x.abbreviation === countryAbbreviation)?.states;

    const returnUrl =
        typeof window !== "undefined"
            ? `${window.location.origin}${window.location.pathname}?sku=${sku}&success=true`
            : "";

    return (
        <StyledWrapper {...styles.wrapper}>
            <LoadingOverlay {...styles.loadingOverlay} loading={isLoading}>
                <form ref={formRef} action={salesforceSubmitUrl} method="POST" onSubmit={submitHandler} noValidate>
                    <input type="hidden" id="description" name="description" value={sku} />
                    <input type="hidden" name="oid" value={salesforceOrganizationId} />
                    <input type="hidden" name="retURL" value={returnUrl} />
                    <input type="hidden" id="recordType" name="recordType" value={recordType} />
                    <input type="hidden" id="00NE0000006IWP2" name="00NE0000006IWP2" value={language} />
                    <input type="hidden" id="country" name="country" value={countryAbbreviation} />
                    <input type="hidden" id="currency" name="currency" value={currencyCode} />
                    <input type="hidden" id="lead_source" name="lead_source" value="Web" />
                    <input type="hidden" id="00N2S000006yhMV" name="00N2S000006yhMV" value="New" />
                    <input type="hidden" id="00N44000006vItL" name="00N44000006vItL" value={siteName} />
                    <GridContainer {...styles.gridContainer}>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={firstNameError}
                                id="first_name"
                                label={firstNameConstants.label()}
                                maxLength={19}
                                name="first_name"
                                onChange={onChangeFirstName}
                                placeholder={firstNameConstants.placeholder()}
                                required={true}
                                value={firstName}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={lastNameError}
                                id="last_name"
                                label={lastNameConstants.label()}
                                maxLength={20}
                                name="last_name"
                                onChange={onChangeLastName}
                                placeholder={lastNameConstants.placeholder()}
                                required={true}
                                value={lastName}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={emailError}
                                id="email"
                                label={emailConstants.label()}
                                maxLength={80}
                                name="email"
                                onChange={onChangeEmail}
                                placeholder={emailConstants.placeholder()}
                                required={true}
                                value={email}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={companyError}
                                id="company"
                                label={companyLabel}
                                maxLength={100}
                                name="company"
                                onChange={onChangeCompany}
                                required={true}
                                value={company}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={phoneError}
                                id="phone"
                                label={phoneConstants.label()}
                                maxLength={phoneConstants.maxLength}
                                name="phone"
                                onChange={onChangePhone}
                                placeholder={phoneConstants.placeholder()}
                                required={true}
                                value={phone}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Select
                                error={stateError}
                                id="state"
                                label={stateLabel}
                                name="state"
                                onChange={onChangeState}
                                required={true}
                                value={state}
                            >
                                <option value="">{statePlaceholder}</option>
                                {states?.map(state => {
                                    return (
                                        <option key={state.abbreviation} value={state.abbreviation}>
                                            {state.name}
                                        </option>
                                    );
                                })}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <TextArea
                                id="00N2S000007MqXj"
                                label={translate("Cansel_Form_Comment")}
                                maxLength={255}
                                name="00N2S000007MqXj"
                                onChange={onChangeComment}
                                value={comment}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <Typography {...styles.agreementMessage}>
                                {siteMessage("Cansel_RequestQuote_Agreement")}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                    <ReCaptcha recaptchaRef={recaptchaRef} location={recaptchaLocation} />
                    <FormActions>
                        <Button {...styles.submitButton} type="submit">
                            {translate("Submit")}
                        </Button>
                    </FormActions>
                </form>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: RequestQuoteForm,
    definition: {
        group: constants.widgetGroup.requestQuote,
        displayName: "Salesforce Request Quote Form",
        icon: "input-pipe",
        allowedContexts: [constants.pageType.requestQuote],
        fieldDefinitions: [],
    },
};

export default widgetModule;
